/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { Component } from "react";
import PropTypes from "prop-types";
import NavigationBarCss from "./NavigationBar.css";
import { PreviousPageNavigationContainer } from "containers/hud/PreviousPageNavigationContainer";
import { NextPageNavigationContainer } from "containers/hud/NextPageNavigationContainer";
import { PageNumberUIContainer } from "containers/hud/PageNumberUIContainer";
import { HorizontalSeparator } from "components/navigationBar/HorizontalSeparator";
import { PageViewSwitcherContainer } from "containers/navigationBar/PageViewSwitcherContainer";
import { ZoomUIContainer } from "containers/hud/ZoomUIContainer";
import { apiFactory } from "api/external/ApiFactory";
import { apiNames } from "constants/apiNames";
import { RotateViewButtonContainer } from "containers/hud/RotateViewButtonContainer";
import { TOOLTIP_LEFT_PLACEMENT } from "constants/constants";
import getDefaultButtonColor from "dc-preview-utils/src/Utils/ThemeColorUtils";
import { BUTTON_STYLES } from "dc-preview-utils/src/Constants/constants";
import { PageThemeFlyoutContainer } from "containers/navigationBar/PageThemeFlyoutContainer";

const shouldEnableHover = true;
const shouldActivate = true;

class NavigationBar extends Component {
  constructor(props) {
    super(props);
    Object.assign(this, props);
    this._registerApis();
    this.state = {
      flyoutPanels: [
        { id: "themeSwitcher", showFlyover: false },
        { id: "viewModeSwitcher", showFlyover: false }
      ]
    };
  }

  handlePanelToggle = (panelId, showFlyover) => {
    this.setState(prevState => ({
      flyoutPanels: prevState.flyoutPanels.map(panel =>
        panel.id === panelId ? { ...panel, showFlyover } : { ...panel, showFlyover: false }
      )
    }));
  };

  _registerApis() {
    apiFactory.registerApi(apiNames.HUD, clientName => ({
      setHUDVisibility: this.props.setHudDisable,
      setPageNavigationVisibility: this.props.setPageNavigationVisibility,
      setPageNumberVisibility: this.props.setPageNumberVisibility,
      setZoomControlsVisibility: this.props.setZoomControlsVisibility,
      setZoomUIVisibility: this.props.setZoomUIVisibility,
      setFitWidthModeVisibility: this.props.setFitWidthModeVisibility,
      setFitPageModeVisibility: this.props.setFitPageModeVisibility,
      setFixedWidthVisibility: this.props.setFixedWidthVisibility,
      setPrintVisibility: this.props.setPrintVisibility,
      setTwoColumnButtonVisibility: this.props.setTwoColumnButtonVisibility,
      setRotateViewButtonVisibility: this.props.setRotateViewButtonVisibility
    }));
  }

  componentWillUnmount() {
    this.props.cleanup();
  }

  componentDidMount() {
    if (this.elementHandle) {
      this.props.init(this.elementHandle);
    }
  }

  componentDidUpdate() {
    if (this.elementHandle) {
      this.props.init(this.elementHandle);
    }
  }

  pageViewModifierUIGroup() {
    const itemList = [];
    (this.props.showZoomUI || this.props.showTwoColumnButton) &&
      itemList.push(
        <PageViewSwitcherContainer
          intl={this.props.intl}
          defaultStyle={getDefaultButtonColor(this.props.currentTheme)}
          selectedStyle={BUTTON_STYLES.SELECTED}
          shouldActivate={shouldActivate}
          activeStyle={BUTTON_STYLES.ACTIVE}
          shouldEnableHover={shouldEnableHover}
          hoverStyle={BUTTON_STYLES.HOVER}
        />
      );
    return itemList;
  }

  getPageNumberUIContainer() {
    return (
      <PageNumberUIContainer
        ref="pageNumberInput"
        intl={this.props.intl}
        totalPagesTooltipPlacement={TOOLTIP_LEFT_PLACEMENT}
        inputTooltipPlacement={TOOLTIP_LEFT_PLACEMENT}
      />
    );
  }

  pageNavigationGroup() {
    const itemList = [];
    const { showPageNavigation, showPageNumber } = this.props;
    showPageNumber && itemList.push(this.getPageNumberUIContainer());
    showPageNavigation &&
      itemList.push(
        <PreviousPageNavigationContainer
          containerStyle={{ marginBottom: "4px" }}
          defaultStyle={getDefaultButtonColor(this.props.currentTheme)}
          shouldActivate={shouldActivate}
          activeStyle={BUTTON_STYLES.ACTIVE}
          shouldEnableHover={shouldEnableHover}
          hoverStyle={BUTTON_STYLES.HOVER}
          tooltipPlacement={TOOLTIP_LEFT_PLACEMENT}
        />
      );
    showPageNavigation &&
      itemList.push(
        <NextPageNavigationContainer
          containerStyle={{ marginTop: "4px" }}
          defaultStyle={getDefaultButtonColor(this.props.currentTheme)}
          shouldActivate={shouldActivate}
          activeStyle={BUTTON_STYLES.ACTIVE}
          shouldEnableHover={shouldEnableHover}
          hoverStyle={BUTTON_STYLES.HOVER}
          tooltipPlacement={TOOLTIP_LEFT_PLACEMENT}
        />
      );
    return itemList;
  }

  zoomUIGroup() {
    const itemList = [];
    this.props.showZoomControl &&
      itemList.push(
        <ZoomUIContainer
          defaultStyle={getDefaultButtonColor(this.props.currentTheme)}
          shouldActivate={shouldActivate}
          activeStyle={BUTTON_STYLES.ACTIVE}
          shouldEnableHover={shouldEnableHover}
          hoverStyle={BUTTON_STYLES.HOVER}
        />
      );
    return itemList;
  }

  getRotateAndPageViewModifierUIElements() {
    const itemList = [];
    this.props.enableThemeSwitcher &&
      itemList.push(
        <PageThemeFlyoutContainer
          intl={this.props.intl}
          showFlyover={this.state.flyoutPanels[0].showFlyover}
          handlePanelToggle={this.handlePanelToggle}
        />
      );

    this.props.showRotateViewButton &&
      itemList.push(
        <RotateViewButtonContainer
          defaultStyle={getDefaultButtonColor(this.props.currentTheme)}
          shouldActivate={shouldActivate}
          activeStyle={BUTTON_STYLES.ACTIVE}
          shouldEnableHover={shouldEnableHover}
          hoverStyle={BUTTON_STYLES.HOVER}
        />
      );
    this.props.showPageViewSwitcher &&
      (this.props.showZoomUI || this.props.showTwoColumnButton) &&
      itemList.push(
        <PageViewSwitcherContainer
          intl={this.props.intl}
          defaultStyle={getDefaultButtonColor(this.props.currentTheme)}
          selectedStyle={BUTTON_STYLES.SELECTED}
          shouldActivate={shouldActivate}
          activeStyle={BUTTON_STYLES.ACTIVE}
          shouldEnableHover={shouldEnableHover}
          hoverStyle={BUTTON_STYLES.HOVER}
          handlePanelToggle={this.handlePanelToggle}
          showFlyover={this.state.flyoutPanels[1].showFlyover}
        />
      );

    return itemList;
  }

  shouldAddSeparatorView(index) {
    return index !== 0;
  }

  getRenderGroupList() {
    const zoomUIGroup = this.zoomUIGroup();
    const pageNavigationGroup = this.pageNavigationGroup();
    const rotateAndPageViewModifierUIGroup = this.getRotateAndPageViewModifierUIElements();
    let groupList = [pageNavigationGroup, rotateAndPageViewModifierUIGroup, zoomUIGroup];
    return groupList.filter(item => item && item.length > 0);
  }

  renderNavigationBarComponents() {
    const navigationBarComponents = [];
    const renderGroupList = this.getRenderGroupList();
    renderGroupList.forEach((component, i) => {
      if (this.shouldAddSeparatorView(i)) {
        navigationBarComponents.push(<HorizontalSeparator />);
      }
      navigationBarComponents.push(component);
    });
    return navigationBarComponents;
  }

  render() {
    return (
      <aside className={NavigationBarCss.NavigationBar} ref={handle => (this.elementHandle = handle)}>
        {this.renderNavigationBarComponents()}
      </aside>
    );
  }
}

export { NavigationBar };
